/**
 * LeadClaimChip — "Claimed by {agent}" indicator + DM handoff button.
 *
 * Shown wherever a lead can be viewed but not edited by the current BDC
 * user — the workspace header and the queue row edit-hover. Clicking the
 * "Request handoff" button opens (or resumes) a DM channel with the
 * current claimant, pre-filling the composer via a query param the
 * BdcTeam page can read.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * Props:
 *   lead     — the lead doc (must have `assigned_bdc_id` + `id`)
 *   claimantName — display name of the claim owner (if known)
 *   claimantId   — user_id of the claim owner
 *   vehicle  — {year, make, model} used in the pre-filled handoff message
 *   compact  — bool, small variant for list rows
 */
const LeadClaimChip = ({ lead, claimantName, claimantId, vehicle, compact = false }) => {
  const nav = useNavigate();
  const [opening, setOpening] = useState(false);

  const handoff = async () => {
    if (opening || !claimantId) return;
    setOpening(true);
    try {
      const { data } = await api.post("/bdc/team/dm", { other_bdc_id: claimantId });
      const preset = encodeURIComponent(
        `Hey ${(claimantName || "").split(" ")[0] || ""} — can I take lead #${lead?.id?.slice(0, 8) || ""}? ` +
        (vehicle ? `Vehicle: ${vehicle.year} ${vehicle.make} ${vehicle.model}, ` : "") +
        `Customer: ${lead?.guest_name || "unknown"}.`
      );
      nav(`/bdc/team?channel=${data.channel_id}&prefill=${preset}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOpening(false);
    }
  };

  const name = claimantName || "another agent";

  if (compact) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-semibold px-2 py-0.5"
        title={`Claimed by ${name}`}
        data-testid="lead-claim-chip-compact"
      >
        <Lock size={9} /> {name}
      </span>
    );
  }

  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3"
      data-testid="lead-claim-chip"
    >
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-900 text-[11px] font-bold px-2.5 py-1">
        <Lock size={11} /> Claimed by {name}
      </span>
      <p className="text-xs text-amber-900 flex-1 min-w-[220px]">
        This lead is locked for editing. You can still view all details — request handoff to make changes.
      </p>
      <button
        type="button"
        onClick={handoff}
        disabled={opening || !claimantId}
        className="inline-flex items-center gap-1.5 rounded-md bg-amber-900 hover:bg-amber-800 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-60"
        data-testid="lead-claim-handoff-btn"
      >
        <MessageCircle size={12} />
        {opening ? "Opening…" : "Send DM to request handoff"}
      </button>
    </div>
  );
};

export default LeadClaimChip;
